/**
 * Single Available-now / Coming-soon shelf inside a catalog tab. Owns its own
 * search input + count badge + empty-state copy. Generic over tile type so
 * the same shell powers Connectors / Knowledge / Models.
 */

import type React from 'react';

import { TileGrid } from './TileGrid.tsx';

export function CatalogSection<T extends { id: string }>({
  title,
  count,
  search,
  onSearchChange,
  placeholder,
  emptyAll,
  emptyFiltered,
  items,
  renderTile,
}: {
  title: string;
  count: number;
  search: string;
  onSearchChange: (q: string) => void;
  placeholder: string;
  emptyAll: string;
  emptyFiltered: (q: string) => string;
  items: T[];
  renderTile: (item: T) => React.ReactNode;
}) {
  const trimmed = search.trim();
  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: '15px',
            fontWeight: 600,
            color: 'var(--olytic-obsidian)',
          }}
        >
          {title}{' '}
          <span
            className="muted"
            style={{ fontWeight: 500, fontSize: '13px', marginLeft: '4px' }}
          >
            ({count})
          </span>
        </h2>
      </div>
      <div style={{ marginBottom: '14px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="search"
          className="form-input"
          placeholder={placeholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ maxWidth: '420px' }}
          aria-label={placeholder}
        />
        {trimmed !== '' && (
          <button
            type="button"
            className="btn-link"
            onClick={() => onSearchChange('')}
            style={{ fontSize: '12px' }}
          >
            Clear
          </button>
        )}
      </div>
      {count === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: '13px' }}>
          {emptyAll}
        </p>
      ) : items.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: '13px' }}>
          {emptyFiltered(trimmed)}
        </p>
      ) : (
        <TileGrid>{items.map(renderTile)}</TileGrid>
      )}
    </section>
  );
}
